<template>
	<div class="auth">
		<div class="auth-container">
			<div class="col-12 col-lg-4 col-md-4">
				<LogoContainer />
			</div>
			<div class="col-12 col-lg-4 col-md-8">
				<div class="auth-card" v-if="isLoginPage">
					<div class="auth-card-header">
						<p class="h2 text-center">/ BUILDlite /</p>
					</div>
					<div class="auth-card-body">
						<form>
							<p class="text-left">
								Sign in with your username and password
							</p>

							<BaseInput
								label="Username"
								v-model="username"
								:errorMsg="validationErrors.username"
							/>
							<BaseInput
								label="Password"
								type="password"
								v-model="password"
								:errorMsg="validationErrors.password"
							/>

							<p class="text-left my-1">
								<a @click.prevent="toForgotPassword"
									>Forgot your password?</a
								>
							</p>
							<button class="btn-login" @click.prevent="signin">
								Sign In
							</button>
							<p class="text-center mt-2">
								Need an account?
								<a @click.prevent="toSignUp">Sign up</a>
							</p>
						</form>
						<!-- Default form subscription -->
					</div>
					<!-- Card body -->
				</div>
				<!-- Card -->
				<div class="auth-card" v-if="isSetNewPassword">
					<div class="auth-card-header">
						<p class="h2 text-center">/ BUILDlite /</p>
					</div>
					<div class="auth-card-body">
						<form>
							<p class="text-left">
								Sign up with your new password and attributes
							</p>
							<BaseInput
								label="New Password"
								type="password"
								v-model="newPassword"
								:errorMsg="validationErrors.newPassword"
							/>
							<BaseInput
								label="Name"
								v-model="name"
								:errorMsg="validationErrors.name"
							/>
							<button
								class="btn-login"
								@click.prevent="setNewPassword"
							>
								Sign Up
							</button>
						</form>
						<!-- Default form subscription -->
					</div>
					<!-- Card body -->
				</div>
				<div class="auth-card" v-if="isConfirmPage">
					<div class="auth-card-header">
						<p class="h2 text-center my-3">/ BUILDlite /</p>
					</div>
					<div class="auth-card-body">
						<form>
							<!-- <p class="h2 text-center py-4 auth-logo">Build Lite</p> -->
							<p class="text-left">
								We have sent a code by email to {{ username }}.
								Enter it below to confirm your account.
							</p>
							<BaseInput
								label="Verification Code"
								type="password"
								v-model="verificationCode"
								:errorMsg="validationErrors.verificationCode"
							/>

							<button
								class="btn-login"
								@click.prevent="confirmingUser"
							>
								Confirm Account
							</button>
							<p class="text-center mt-2">
								Didn't receive a code?
								<a @click.prevent="resendConfirmationCode"
									>Resend it</a
								>
							</p>
						</form>
						<!-- Default form subscription -->
					</div>
					<!-- Card body -->
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import CognitoService from '@/services/CognitoService.js'
import { isStaff } from '@/utils/Help.js'
import * as yup from 'yup'
import { getValidationErrors } from '@/utils/Help.js'
import NProgress from 'nprogress'
import LogoContainer from '@/components/LogoContainer.vue'

const loginSchema = yup.object().shape({
	username: yup
		.string()
		.label('User Name')
		.required("User Name can't be empty."),
	password: yup
		.string()
		.label('Password')
		.required("Password can't be empty.")
})

const confirmingUserScheme = yup.object().shape({
	verificationCode: yup
		.number()
		.typeError('Verification Code must be a number')
		.label('Verification Code')
		.required("Verification Code can't be empty.")
})

const setNewPasswordSchema = yup.object().shape({
	newPassword: yup
		.string()
		.label('New Password')
		.required("New Password can't be empty."),
	name: yup
		.string()
		.label('Name')
		.required("Name can't be empty.")
})

export default {
	name: 'Login',
	inject: ['reload'],
	computed: {
		isLoginPage() {
			return !this.isSetNewPassword && !this.isConfirmPage
		}
	},
	components: {
		LogoContainer
	},
	data() {
		return {
			username: '',
			password: '',
			newPassword: '',
			name: '',
			isSetNewPassword: false,
			verificationCode: '',
			isConfirmPage: false,
			validationErrors: {}
		}
	},
	methods: {
		async signin() {
			const userInput = {
				username: this.username,
				password: this.password
			}
			NProgress.start()
			try {
				await loginSchema.validate(userInput, {
					abortEarly: false
				})
				await CognitoService.login(
					this.username.replace(/\s+/g, ''),
					this.password.replace(/\s+/g, '')
				)
				const routeToPath = this.$store.state.auth.routeToPath
				NProgress.done()
				if (routeToPath) {
					this.$router.push({
						path: routeToPath
					})
				} else {
					if (isStaff()) {
						this.$router.push({
							name: 'job-list'
						})
					} else {
						this.$router.push({
							name: 'home'
						})
					}
				}
			} catch (err) {
				console.log(err)
				if (err.name === 'ValidationError') {
					this.validationErrors = getValidationErrors(err)
				}

				if (err.code === 'UserNotConfirmedException') {
					this.isConfirmPage = true
				} else if (err.code === 'needCreateNewPassword') {
					this.isSetNewPassword = true
				}
				NProgress.done()
			}
		},
		toSignUp() {
			this.$router.push({ name: 'sign-up' })
		},
		toForgotPassword() {
			this.$router.push({ name: 'forgot-password' })
		},
		async setNewPassword() {
			NProgress.start()
			let attributesData = {
				name: this.name,
				profile: 'Staff'
			}

			const userInput = {
				newPassword: this.newPassword,
				name: this.name
			}

			try {
				await setNewPasswordSchema.validate(userInput, {
					abortEarly: false
				})
				await CognitoService.login(
					this.username,
					this.password,
					this.newPassword,
					attributesData
				)
				NProgress.done()
				this.$router.push({
					name: 'home'
				})
			} catch (err) {
				console.log(err)
				if (err.name === 'ValidationError') {
					this.validationErrors = getValidationErrors(err)
				}
				NProgress.done()
			}
		},
		async confirmingUser() {
			NProgress.start()
			const userInput = {
				verificationCode: this.verificationCode
			}
			try {
				await confirmingUserScheme.validate(userInput, {
					abortEarly: false
				})
				const result = await CognitoService.confirmingUser(
					this.username,
					this.password,
					this.verificationCode
				)
				NProgress.done()
				if (result) {
					this.reload()
				}
			} catch (err) {
				if (err.name === 'ValidationError') {
					this.validationErrors = getValidationErrors(err)
				}
				NProgress.done()
				console.log(err)
			}
		},
		async resendConfirmationCode() {
			NProgress.start()
			try {
				await CognitoService.resendConfirmationCode(
					this.username,
					this.password
				)
				NProgress.done()
			} catch (e) {
				console.log(e)
				NProgress.done()
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../styles/views/auth.scss';
</style>
